<script>
import { useVuelidate } from '@vuelidate/core';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import ButtonNext from 'next/button/Button.vue';
import IconNext from 'next/icon/Icon.vue';
import validationMixins from './helpers/validationMixins';

function hasAnyLabel(field) {
  if (field.controls) {
    return field.controls.some(hasAnyLabel);
  }
  return !!field.label;
}

export default {
  components: {
    ButtonNext,
    IconNext,
  },
  mixins: [validationMixins],
  props: {
    field: {
      type: Object,
      required: true,
    },
    values: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    fixedItems: {
      type: Array,
      default: () => [],
    },
    loadingFixedItems: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      v$: useVuelidate(),
      toLocale,
    };
  },
  data() {
    return {
      arrayValue: this.values ?? this.field.default,
    };
  },
  computed: {
    hasFixedItems() {
      return !!this.field.fixed_controls;
    },
    shouldClearFixedItems() {
      return (
        this.hasFixedItems && !this.loadingFixedItems && !this.fixedItems.length
      );
    },
    description() {
      return this.field.description;
    },
    hasAnyLabel() {
      return hasAnyLabel(this.field);
    },
    required() {
      return this.field.required;
    },
    min() {
      if (this.field.min != null) {
        return this.field.min;
      }
      if (this.field.required) {
        return 1;
      }

      return undefined;
    },
    descriptionTooltip() {
      if (!this.description) return null;

      return {
        content: this.toLocale(this.description),
        popperClass:
          'max-w-[300px] max-h-[300px] overflow-y-auto whitespace-pre-line',
        boundary: 'body',
        popperTriggers: ['hover'],
      };
    },
    disableRemoveButton() {
      if (this.disabled) return true;
      if (this.min === undefined) return false;

      return this.arrayValue.length <= this.min;
    },
  },
  validations() {
    return {
      arrayValue: {
        required: arrayValue => {
          if (!this.required) return true;

          return arrayValue.length > 0;
        },
        duplicate: this.duplicateIf(!this.hasFixedItems, this.field),
      },
    };
  },
  watch: {
    values: {
      handler(values) {
        if (!this.hasFixedItems) {
          this.arrayValue = values;
        }
      },
      immediate: true,
    },
    shouldClearFixedItems(shouldClear) {
      if (shouldClear && this.arrayValue?.length) {
        this.arrayValue = [];
        this.$emit('change', []);
      }
    },
    fixedItems: {
      handler(fixedItems) {
        if (!this.hasFixedItems) return;
        if (!fixedItems.length) return;

        const existingValues = this.arrayValue || [];
        const newArrayValue = fixedItems.map(fixedItem => {
          const flatControls = this.extractFlatControls(fixedItem.controls);
          const entry = {};
          flatControls.forEach(c => {
            entry[c.name] = c.value ?? c.default ?? '';
          });

          const matchKey = flatControls.find(
            c => c.disabled && c.value != null
          );
          if (matchKey) {
            const existing = existingValues.find(
              ev => ev[matchKey.name] === entry[matchKey.name]
            );
            if (existing) {
              flatControls.forEach(c => {
                if (!c.disabled && existing[c.name] != null) {
                  entry[c.name] = existing[c.name];
                }
              });
            }
          }

          return entry;
        });

        this.arrayValue = newArrayValue;
        this.$emit('change', newArrayValue);
      },
      deep: true,
    },
  },
  mounted() {
    if (this.dirty) {
      this.$nextTick(() => {
        this.v$.$validate();
      });
    }
  },
  methods: {
    extractFlatControls(controls) {
      return controls.reduce((flat, control) => {
        if (control.type === 'group' && control.controls) {
          return flat.concat(control.controls);
        }
        return flat.concat(control);
      }, []);
    },
    arrayControlChange(value, index) {
      const arrayValue = this.arrayValue.map((item, i) => {
        if (i === index) return { ...item, ...value };
        return item;
      });
      this.arrayValue = arrayValue;
      this.v$.arrayValue.$touch();
      this.$emit('change', arrayValue);
    },
    removeArrayControlAt(index) {
      if (this.field.min && this.arrayValue.length <= this.field.min) return;

      const arrayValue = this.arrayValue.filter((_, i) => i !== index);
      this.arrayValue = arrayValue;
      this.v$.arrayValue.$touch();
      this.$emit('change', arrayValue);
    },
    addArrayControl() {
      if (this.field.max && this.arrayValue.length >= this.field.max) return;

      const arrayControls = Array.from(this.arrayValue);
      this.field.controls.forEach(control => {
        if (control.type === 'group') {
          const groupControl = {};
          control.controls.forEach(c => {
            groupControl[c.name] = c.default || '';
          });
          arrayControls.push(groupControl);
        }
      });

      this.arrayValue = arrayControls;
      this.v$.arrayValue.$touch();
      this.$emit('change', arrayControls);
    },
  },
};
</script>

<template>
  <div
    class="array-field"
    :class="{ 'array-field-error': v$.arrayValue.$error }"
  >
    <label
      v-if="
        (field.label || descriptionTooltip) &&
        (!hasFixedItems || fixedItems.length)
      "
      class="flex items-center gap-2 leading-none"
    >
      <span v-if="field.label" class="block truncate">{{
        toLocale(field.label, field.fallbackLabel)
      }}</span>
      <IconNext
        v-if="descriptionTooltip"
        v-tooltip.top="descriptionTooltip"
        icon="i-lucide-info"
        class="text-n-slate-11 size-3.5"
      />
    </label>

    <!-- Fixed items (auto-populated from dynamic array) -->
    <div
      v-if="fixedItems.length && arrayValue.length"
      class="array-fields p-4 pe-2 rounded-xl bg-n-alpha-2 dark:bg-n-solid-2 flex flex-col gap-1"
    >
      <div
        v-for="(fixedItem, index) of fixedItems"
        :key="'fixed-' + index"
        class="array-field-item"
      >
        <div class="flex gap-2">
          <slot
            :value="arrayValue[index]"
            :controls="fixedItem.controls"
            :change="value => arrayControlChange(value, index)"
            :disabled="disabled"
          />
        </div>

        <div class="separator" />
      </div>
    </div>

    <!-- Loading fixed items -->
    <div
      v-else-if="loadingFixedItems"
      class="p-4 rounded-xl bg-n-alpha-2 dark:bg-n-solid-2 space-y-4 animate-loader-pulse"
    >
      <div class="space-y-2">
        <div class="h-3 bg-n-alpha-3 dark:bg-n-alpha-2 rounded w-1/4" />
        <div class="h-10 bg-n-alpha-3 dark:bg-n-alpha-2 rounded" />
      </div>
      <div class="space-y-2">
        <div class="h-3 bg-n-alpha-3 dark:bg-n-alpha-2 rounded w-1/4" />
        <div class="h-10 bg-n-alpha-3 dark:bg-n-alpha-2 rounded" />
      </div>
    </div>

    <!-- Regular items -->
    <div
      v-else-if="arrayValue.length"
      class="array-fields p-4 pe-2 rounded-xl bg-n-alpha-2 dark:bg-n-solid-2 flex flex-col gap-1"
    >
      <div
        v-for="(arrayValueItem, index) of arrayValue"
        :key="index"
        class="array-field-item"
      >
        <div class="flex gap-2">
          <slot
            :value="arrayValueItem"
            :change="value => arrayControlChange(value, index)"
            :disabled="disabled"
          />
          <div>
            <div
              class="h-10 flex items-center"
              :class="{ 'mt-6': hasAnyLabel }"
            >
              <ButtonNext
                size="xs"
                variant="ghost"
                icon="i-lucide-x"
                color="slate"
                class="shrink-0"
                :disabled="disableRemoveButton"
                @click="removeArrayControlAt(index)"
              />
            </div>
          </div>
        </div>

        <div class="separator" />
      </div>
    </div>

    <p
      v-if="v$.arrayValue.$error"
      class="min-w-0 my-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
    >
      <template v-if="v$.arrayValue.required.$invalid">
        {{ $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.REQUIRED') }}
      </template>
      <template v-else-if="v$.arrayValue.duplicate.$invalid">
        {{ v$.arrayValue.duplicate.$message }}
      </template>
      <template v-else>
        {{ $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.INVALID') }}
      </template>
    </p>

    <div v-if="!hasFixedItems">
      <ButtonNext
        size="sm"
        icon="i-lucide-plus"
        :disabled="disabled"
        @click="addArrayControl"
      >
        {{ toLocale(field.add_label || 'FORM.ARRAY.ADD') }}
      </ButtonNext>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.array-field {
  @apply flex flex-col gap-3;
}

.array-field-error .array-fields {
  @apply bg-n-ruby-3;
}

.separator {
  @apply border-t border-n-strong my-6;
}

.array-field-item:last-child .separator {
  @apply hidden;
}
</style>
